import { useMemo } from 'react';

/**
 * Hook to extract and process filter aggregations from API response
 * @param {Object} data - API response data containing filter_options
 * @param {Object} filters - Current filter values
 * @returns {Object} Processed aggregations with options and counts
 */
export const useFilterAggregations = (data, filters = {}) => {
  return useMemo(() => {
    if (!data?.filter_options) {
      return {};
    }

    const filterOptions = {};
    const filterCounts = {};

    // Process each aggregation from the API response
    Object.entries(data.filter_options).forEach(([filterKey, options]) => {
      if (!Array.isArray(options) || options.length === 0) {
        return;
      }

      // Convert aggregation results to select options
      // Backend returns {value, label, count} format from _extract_buckets method
      filterOptions[filterKey] = options.map(option => ({
        value: option.value,
        label: option.label,
        count: option.count || 0,
      }));

      // Track filter counts for dynamic hiding
      filterCounts[filterKey] = options.length;
    });

    return {
      filterOptions,
      filterCounts,
      hasOptions: (filterKey) => (filterCounts[filterKey] || 0) > 1, // Only show if more than 1 option
      getOptions: (filterKey) => filterOptions[filterKey] || [],
      getCount: (filterKey) => filterCounts[filterKey] || 0,
    };
  }, [data?.filter_options, filters]);
};